/// <amd-module name="Release/Landing/LeftPanel/AllPipelines/AllDefinitionsStore" />
import { StoreBase } from "DistributedTask/Flux/Store";

import { ObservableValue } from "VSS/Core/Observable";
import { ITreeItemProvider as ITreeItemExProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IVssPageContext } from "VSS/Platform/Context";

import { Folder, ReleaseDefinition } from "Release/Client/Contracts/Release";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { INode, IAllDefinitionsProviderData, NodeType } from "./Types";
import { AllDefinitionsActions, IRenameFolderPayload, IPipelineMovedPayload } from "./AllDefinitionsActions";
import { DefinitionsTreeExProvider } from "./DefinitionsTreeProvider";
import { FolderUtils } from "./FolderUtils";
import { PipelinesActions, IRemovePipelinePayload } from "../PipelinesActions";
import { getDefinitionReferenceFromDefinition } from "LandingUtils";

export var AllPipelinesItemDeleted = "ALL_PIPELINES_ITEM_DELETED"

export class AllDefinitionsStore extends StoreBase {

    public constructor(private _pageContext: IVssPageContext) {
        super("");
    }

    public static getKey(): string {
        return "release-landing-all-definitions-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);

        this._pathToChildrenCache = {};
        this._pathToChildrenCache2 = {};
        this._pathToExpandedCache = {};
        this._fetchingPipelines = false; // Initialize as false to enable the call to getChildren in DefinitionsTreeProvider
        this._actionsHub = ActionsHubManager.GetActionsHub<AllDefinitionsActions>(AllDefinitionsActions);
        this._pipelinesActions = ActionsHubManager.GetActionsHub<PipelinesActions>(PipelinesActions);

        this._actionsHub.folderCreated.addListener(this._onFolderCreated);
        this._actionsHub.pipelineRenamed.addListener(this._onPipelineRenamed);
        this._actionsHub.pipelineMoved.addListener(this._onPipelineMoved);
        this._actionsHub.folderRenamed.addListener(this._onFolderRenamed);
        this._actionsHub.pipelinesFetched.addListener(this._onPipelinesFetched);
        this._actionsHub.fetchingPipelines.addListener(this._loadingTree);
        this._actionsHub.folderToggled.addListener(this._onFolderToggled);
        this._pipelinesActions.clearData.addListener(this._clearFolders);
        this._pipelinesActions.removeFolder.addListener(this._onFolderDeleted);
        this._pipelinesActions.removePipeline.addListener(this._onRemovePipeline);
    }

    public disposeInternal(): void {
        this._actionsHub.folderCreated.removeListener(this._onFolderCreated);
        this._actionsHub.pipelineRenamed.removeListener(this._onPipelineRenamed);
        this._actionsHub.pipelineMoved.removeListener(this._onPipelineMoved);
        this._actionsHub.pipelinesFetched.removeListener(this._onPipelinesFetched);
        this._actionsHub.fetchingPipelines.removeListener(this._loadingTree);
        this._actionsHub.folderRenamed.removeListener(this._onFolderRenamed);
        this._actionsHub.folderToggled.removeListener(this._onFolderToggled);
        this._pipelinesActions.clearData.removeListener(this._clearFolders);
        this._pipelinesActions.removeFolder.removeListener(this._onFolderDeleted);
        this._pipelinesActions.removePipeline.removeListener(this._onRemovePipeline);
    }

    public isLoadingTreeProvider(): boolean {
        return this._fetchingPipelines;
    }

    private _onFolderCreated = (folder: Folder): void => {
        const parentFolderPath: string = FolderUtils.getParentFolderPath(folder);

        // delete the entry in cache to force fetch from server
        this._deleteKeyFromCaches(parentFolderPath);
        this.emitChanged();
    }

    private _onPipelineRenamed = (renamedDefinition: ReleaseDefinition): void => {
        if (this._pathToChildrenCache.hasOwnProperty(renamedDefinition.path)) {
            // update the promise for the parent/folder of renamedDefinition to include the updated definition entry.
            const newPromise = Promise.resolve(this._pathToChildrenCache[renamedDefinition.path]).then((children: INode[]) => {
                const index = this._getDefinitionIndex(children, renamedDefinition.id);
                if (index > -1) {
                    const existingDef = children[index].releaseDefinition;
                    children[index].releaseDefinition = getDefinitionReferenceFromDefinition(renamedDefinition, existingDef.definitionType, existingDef.lastDeployment, existingDef.pendingApproval);
                }

                return children;
            });

            newPromise.then(() => {
                this.emitChanged();
            });
        }
    }

    private _onRemovePipeline = (payload: IRemovePipelinePayload): void => {
        if (this._pathToChildrenCache.hasOwnProperty(payload.pipelinePath)) {
            const newPromise = Promise.resolve(this._pathToChildrenCache[payload.pipelinePath]).then((children: INode[]) => {
                const index = this._getDefinitionIndex(children, payload.pipelineId);
                if (index > -1) {
                    children.splice(index, 1);
                }

                return children;
            });

            newPromise.then(() => {
                this._pathToChildrenCache[payload.pipelinePath] = newPromise;
                this.emitChanged();
                this.emit(AllPipelinesItemDeleted, {});
            });
        }
    }

    private _onPipelineMoved = (payload: IPipelineMovedPayload): void => {
        // clear out the existing promises, if any. if the folders are already expanded, the tree will refresh them from the server
        // if they're not already expanded, this is a no-op
        this._deleteKeyFromCaches(payload.originalPath);
        this._deleteKeyFromCaches(payload.movedPipeline.path);

        // if the definition was moved to a new folder...
        let changedPromise: Promise<void> | undefined = undefined;
        const segments = this._splitPath(payload.movedPipeline.path);
        for (let i = segments.length - 1; i > 0; --i) {
            const parentPath = "\\" + segments.slice(0, i).join("\\");
            const parentPromise = this._pathToChildrenCache[parentPath];
            if (parentPromise) {
                changedPromise = Promise.resolve(parentPromise).then((items: INode[]) => {
                    if (items.findIndex((item) => !!item.folder && item.folder.path === payload.movedPipeline.path) === -1) {
                        // the new path doesn't exist in its parent. reload the parent
                        this._deleteKeyFromCaches(parentPath);
                    }
                });
                break;
            }
        }

        // TODO: if a definition was moved to or from a folder and one of its ancestors, the subfolders will end up collapsed.
        // figure out how to re-expand them

        // only emit once
        if (changedPromise) {
            changedPromise.then(() => this.emitChanged());
        }
        else {
            this.emitChanged();
        }
    }

    private _splitPath(path: string): string[] {
        const result: string[] = [];
        (path || "").split("\\").forEach((segment) => {
            if (segment.length > 0) {
                result.push(segment);
            }
        });

        return result;
    }

    private _onFolderRenamed = (payload: IRenameFolderPayload): void => {
        const parentFolderPath: string = FolderUtils.getParentFolderPath(payload.folder);
        this._pathToChildrenCache[parentFolderPath]
            = Promise.resolve(this._pathToChildrenCache[parentFolderPath])
                .then((nodes: INode[]) => {
                    const renamedIndex = nodes.findIndex(node => node.type === NodeType.Folder && node.folder.path === payload.oldPath);
                    if (renamedIndex >= 0) {
                        nodes[renamedIndex].folder = payload.folder;
                    }

                    this._deleteChildrenFromCache(payload.oldPath);
                    this.emitChanged();

                    return nodes;
                });
    }

    private _onFolderDeleted = (payload: string): void => {
        const parentFolderPath: string = payload.substring(0, payload.lastIndexOf("\\")) || "\\";
        this._pathToChildrenCache[parentFolderPath]
            = Promise.resolve(this._pathToChildrenCache[parentFolderPath])
                .then((nodes: INode[]) => {
                    const deletedIndex = nodes.findIndex(node => node.type === NodeType.Folder && node.folder.path === payload);
                    if (deletedIndex >= 0) {
                        nodes.splice(deletedIndex, 1);
                    }

                    this._deleteChildrenFromCache(payload);
                    this.emitChanged();
                    this.emit(AllPipelinesItemDeleted, {});

                    return nodes;
                });
    }

    private _onPipelinesFetched = (): void => {
        this._fetchingPipelines = false;
        // _pathToChildrenCache is already updated TreeProvider. Simply emit change here so that PipelinesStore can update itself
        this.emitChanged();
    }

    private _loadingTree = (): void => {
        this._fetchingPipelines = true;
        this.emitChanged();
    }

    private _clearFolders = () => {
        this._pathToChildrenCache = {};
        this._pathToChildrenCache2 = {};
        this._pathToExpandedCache = {};
    }

    private _onFolderToggled = () => this.emitChanged();

    public getState(): { [key: string]: INode[] | Promise<INode[]> } {
        return this._pathToChildrenCache;
    }

    public isDefinitionIdResolvedChild(definitionId: number, folderPath: string): Promise<boolean> {
        if (!this._pathToChildrenCache[folderPath]) {
            return Promise.resolve(false);
        }

        const isPresentInGivenNodes = (nodes: INode[]) => {
            let isPresent = nodes.some(node => node.releaseDefinition && node.releaseDefinition.id === definitionId);
            if (isPresent) {
                return isPresent;
            }

            return Promise.all(nodes.filter(node => !!node.folder).map(node => this.isDefinitionIdResolvedChild(definitionId, node.folder.path)))
                .then((results: boolean[]) => {
                    return results.some(res => res);
                });
        }

        const nodes = this._pathToChildrenCache[folderPath];

        // The dictionary stores both promises and direct objects. We have to treat these two cases seperately
        if (typeof (nodes as Promise<INode[]>).then !== "function") {
            return Promise.resolve(isPresentInGivenNodes(nodes as INode[]));
        }

        return (nodes as Promise<INode[]>).then((nodes) => {
            return isPresentInGivenNodes(nodes);
        });
    }

    public getDefinitionTreeExProvider(updatingStore: boolean): ITreeItemExProvider<ObservableValue<INode>> {
        // Whenever a new TreeProvider is returned, the tree will be redrawn.
        return new DefinitionsTreeExProvider(this._pageContext, this._pathToChildrenCache, this._pathToChildrenCache2, this._pathToExpandedCache, updatingStore);
    }

    private _getDefinitionIndex(nodes: INode[], definitionId: number): number {
        return nodes.findIndex((node) => {
            return !!node.releaseDefinition && node.releaseDefinition.id === definitionId;
        });
    }

    private _deleteChildrenFromCache(folderPath: string): void {
        let keysToDelete: string[] = [];
        Object.keys(this._pathToChildrenCache).forEach((key) => {
            // Remove all entries that start with deleted folder id
            if (key.indexOf(folderPath) === 0) {
                keysToDelete.push(key);
            }
        });

        keysToDelete.forEach((key) => this._deleteKeyFromCaches(key, true));
    }

    private _deleteKeyFromCaches(folderPath: string, deleteFromExpanded?: boolean): void {
        if (deleteFromExpanded) {
            delete this._pathToExpandedCache[folderPath];
        }

        delete this._pathToChildrenCache[folderPath];
        delete this._pathToChildrenCache2[folderPath];
    }

    private _actionsHub: AllDefinitionsActions;
    private _pipelinesActions: PipelinesActions;
    private _pathToChildrenCache: { [key: string]: INode[] | Promise<INode[]> };
    private _pathToChildrenCache2: { [key: string]: INode[] };
    private _pathToExpandedCache: { [key: string]: boolean };
    private _fetchingPipelines: boolean;
}
